import { FlowClassName, ResultsMode } from 'interfaces/experimentResults';
import { Study } from 'interfaces/study';

// `latest` is Each slide's latest run: every slide takes its own newest run, so no run is named here.
export type WorkflowResultsSource = 'published' | 'approved' | 'latest' | 'run';
export type WorkflowExportKind = 'features' | 'cellTables';

// Both Workflow export dialogs offer these post-processing results. Normalization and other flow classes are not.
export const isWorkflowPostProcessingResult = (flowClassName?: string) =>
  flowClassName === FlowClassName.CalculateFeatures;

export interface WorkflowResultsSourceSelection {
  source: WorkflowResultsSource;
  // Undefined means no run has been chosen yet; an empty list means the user cleared the choice.
  runIds?: string[];
}

const kindLabel: Record<WorkflowExportKind, string> = { features: 'features', cellTables: 'cell tables' };

// hasFeatures and hasResults count published results only; the unpublished fields come from the study read with
// statistics and are only set for users who may view unpublished results.
const hasPublished = (kind: WorkflowExportKind, study: Study) =>
  Boolean(kind === 'features' ? study.hasFeatures : study.hasResults);
const hasUnpublished = (kind: WorkflowExportKind, study: Study) =>
  Boolean(kind === 'features' ? study.hasUnpublishedFeatures : study.hasUnpublishedCellTables);
// Internally approved and not yet published: what the Approved option adds to Published.
const hasInternallyApproved = (kind: WorkflowExportKind, study: Study) =>
  Boolean(kind === 'features' ? study.hasInternallyApprovedFeatures : study.hasInternallyApprovedCellTables);

// A Workflow export button exists only when the study has something to export for this user.
export const getWorkflowExportVisibility = ({
  kind,
  study,
  canViewUnpublishedResults,
  canDownloadCellsTable,
}: {
  kind: WorkflowExportKind;
  study?: Study;
  canViewUnpublishedResults: boolean;
  canDownloadCellsTable: boolean;
}) => {
  if (!study?.allowExport) return false;
  if (kind === 'cellTables' && !canDownloadCellsTable) return false;
  return hasPublished(kind, study) || (canViewUnpublishedResults && hasUnpublished(kind, study));
};

export interface WorkflowResultsModeParams {
  resultsMode: ResultsMode;
  // Empty unless the mode is Manual; each run is requested on its own.
  orchestrationIds: string[];
}

export const toWorkflowResultsModeParams = (
  { source, runIds }: WorkflowResultsSourceSelection,
  canViewUnpublishedResults: boolean
): WorkflowResultsModeParams => {
  if (!canViewUnpublishedResults || source === 'published') {
    return { resultsMode: ResultsMode.Published, orchestrationIds: [] };
  }
  if (source === 'approved') return { resultsMode: ResultsMode.InternallyApproved, orchestrationIds: [] };
  return { resultsMode: ResultsMode.Manual, orchestrationIds: source === 'latest' ? [] : runIds ?? [] };
};

export interface WorkflowSourceAvailability {
  disabled: boolean;
  // Why the option is unavailable, or what is still needed to use it.
  note?: string;
}

export const workflowResultsCheckingMessage = 'Checking which results are available…';
export const workflowRunsFailedNote = 'Post-processing runs could not be loaded.';
// Shown by a download that has no chooser, when the failed run list leaves nothing it can export.
export const workflowDirectRunsFailedGuidance =
  'The post-processing runs could not be loaded, so the results to export are unknown. Close this and try again, or use Download specific version/s.';

// True when at least one results source has something to export for this user: the study flags that decide whether
// the Workflow export button is shown, plus the post-processing runs the dialog loads.
export const hasWorkflowResults = ({
  kind,
  study,
  runCount,
  canViewUnpublishedResults,
}: {
  kind: WorkflowExportKind;
  study: Study;
  runCount: number;
  canViewUnpublishedResults: boolean;
}) =>
  hasPublished(kind, study) ||
  (canViewUnpublishedResults && (hasUnpublished(kind, study) || hasInternallyApproved(kind, study) || runCount > 0));

export const getNoWorkflowResultsNote = (kind: WorkflowExportKind) =>
  `No ${kindLabel[kind]} are available to export for this study.`;

export const getWorkflowSourceAvailability = ({
  kind,
  selection,
  study,
  runCount,
  canViewUnpublishedResults,
}: {
  kind: WorkflowExportKind;
  selection: WorkflowResultsSourceSelection;
  study: Study;
  runCount: number;
  canViewUnpublishedResults: boolean;
}): WorkflowSourceAvailability => {
  switch (selection.source) {
    case 'published': {
      if (hasPublished(kind, study)) return { disabled: false };
      // Only name the ways forward this user has; unpublished options are not theirs to know about.
      const alternatives = canViewUnpublishedResults
        ? [
            ...(hasInternallyApproved(kind, study) ? ['approved results'] : []),
            ...(runCount > 0 ? ['a post-processing run'] : []),
          ]
        : [];
      return {
        disabled: true,
        note: `No published ${kindLabel[kind]}.${alternatives.length ? ` Choose ${alternatives.join(' or ')}.` : ''}`,
      };
    }
    case 'approved':
      return hasInternallyApproved(kind, study)
        ? { disabled: false }
        : { disabled: true, note: `No approved ${kindLabel[kind]} that are not yet published.` };
    case 'latest':
      return runCount === 0 ? { disabled: true, note: 'No post-processing runs for this study.' } : { disabled: false };
    default:
      // A specific post-processing run is chosen on each panel, not by a study-wide run list.
      return runCount === 0 ? { disabled: true, note: 'No post-processing runs for this study.' } : { disabled: false };
  }
};

// The results source a Workflow export dialog shows. A restored dialog choice wins. Otherwise Approved when any
// approved result exists, otherwise Published when any published result exists, otherwise Each slide's latest run.
// The Workflow tab's run is not an input and is not copied in. A specific-run choice does not carry a study-wide
// run list; each panel stores its own orchestration ids.
export const resolveWorkflowResultsSource = ({
  kind,
  study,
  canViewUnpublishedResults,
  chosen,
  runCount,
  isLoadingRuns,
}: {
  kind: WorkflowExportKind;
  study: Study;
  canViewUnpublishedResults: boolean;
  chosen?: WorkflowResultsSourceSelection;
  runCount: number;
  isLoadingRuns: boolean;
}): WorkflowResultsSourceSelection => {
  if (!canViewUnpublishedResults) return { source: 'published' };
  const isUsable = (selection: WorkflowResultsSourceSelection) =>
    selection.source === 'run' || selection.source === 'latest'
      ? runCount > 0 || isLoadingRuns
      : !getWorkflowSourceAvailability({ kind, selection, study, runCount, canViewUnpublishedResults }).disabled;

  if (chosen && isUsable(chosen)) return chosen.source === 'run' ? { source: 'run' } : { source: chosen.source };
  if (isUsable({ source: 'approved' })) return { source: 'approved' };
  if (isUsable({ source: 'published' })) return { source: 'published' };
  if (runCount > 0 || isLoadingRuns) return { source: 'latest' };
  return { source: 'published' };
};
